import { CSSProperties, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { createPlayer } from "@videojs/react";
import { Video, VideoSkin, videoFeatures } from "@videojs/react/video";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { PLAYBACK_RATES } from "@/domains/certifications/constants";
import { usePlaybackSession } from "@/domains/certifications/hooks/usePlaybackSession";
import { getVideoDetails, toBackendUrl } from "@/domains/certifications/utils";
import { isVideoJustWatched } from "@/domains/certifications/progress";
import { PlaybackProgress } from "@/domains/certifications/types";
import { VideoPlayerModalProps } from "@/features/certifications/module/components/types";

import "@videojs/react/presets/video/skin.css";
import "./styles.css";

const MODAL_WIDTH = 960;
const CHECK_ICON_SIZE = 18;
const CONFETTI_COLORS = [
  "var(--primary-600)",
  "var(--primary-400)",
  "var(--orange-500)",
  "var(--green-500)",
  "var(--orange-300)",
];
const CONFETTI_PIECES = Array.from({ length: 16 }, (_, index) => {
  const angle = (2 * Math.PI * index) / 16;
  const distance = index % 2 === 0 ? 44 : 30;
  return {
    x: Math.round(Math.cos(angle) * distance),
    // Flattened so the burst stays inside the banner, clear of the video above it.
    y: Math.round(Math.sin(angle) * distance * 0.45),
    color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
    delay: (index % 4) * 30,
  };
});

// The skin's speed menus list the store's playbackRates, which Video.js hard-codes.
const { Player: CertificationVideoPlayer } = createPlayer({
  features: videoFeatures.map((feature) =>
    feature.name === "playbackRate"
      ? {
          ...feature,
          state: (context: Parameters<typeof feature.state>[0]) => ({
            ...feature.state(context),
            playbackRates: PLAYBACK_RATES,
          }),
        }
      : feature,
  ),
  displayName: "CertificationVideoPlayer",
});

const VideoPlayerModal = ({
  certificationSlug,
  selection,
  onClose,
}: VideoPlayerModalProps) => {
  const { t } = useTranslation();
  const shownRef = useRef(selection);
  if (selection) {
    shownRef.current = selection;
  }
  const shown = shownRef.current;
  const thumbnailUrl = shown
    ? getVideoDetails(shown.video)?.thumbnailUrl
    : null;
  const poster = thumbnailUrl ? toBackendUrl(thumbnailUrl) : undefined;
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const {
    videoRef,
    playbackUrl,
    progress,
    isStarting,
    isError,
    isTrackingStopped,
  } = usePlaybackSession({
    certificationSlug,
    contentItemId: selection?.video.id ?? null,
  });

  const [seenProgress, setSeenProgress] = useState<PlaybackProgress | null>(
    progress,
  );
  const [isCelebrating, setIsCelebrating] = useState(false);
  if (progress !== seenProgress) {
    if (!progress) {
      setIsCelebrating(false);
    } else if (isVideoJustWatched(seenProgress, progress)) {
      setIsCelebrating(true);
    }
    setSeenProgress(progress);
  }

  let status = null;
  if (isTrackingStopped) {
    status = (
      <p className="module-warning-text body-s-regular">
        {t("certification.module.player.trackingStopped")}
      </p>
    );
  } else if (progress?.isComplete) {
    status = (
      <p
        className={`module-player-watched flex items-center gap-200 body-s-medium ${
          isCelebrating ? "module-player-watched-celebrate" : ""
        }`}
      >
        <Check size={CHECK_ICON_SIZE} className="flex-none" />

        {isCelebrating && (
          <span className="module-player-confetti" aria-hidden>
            {CONFETTI_PIECES.map((piece) => (
              <span
                key={`${piece.x}-${piece.y}`}
                style={
                  {
                    "--confetti-x": `${piece.x}px`,
                    "--confetti-y": `${piece.y}px`,
                    backgroundColor: piece.color,
                    animationDelay: `${piece.delay}ms`,
                  } as CSSProperties
                }
              />
            ))}
          </span>
        )}

        {t("certification.module.player.watched")}
      </p>
    );
  } else if (progress) {
    status = (
      <p className="certification-muted body-s-regular">
        {t("certification.module.player.coveredPercent", {
          percent: Math.floor(progress.coveredPercent),
        })}
      </p>
    );
  }

  return (
    <BaseModal
      open={selection !== null}
      width={MODAL_WIDTH}
      onCancel={onClose}
      destroyOnClose
      title={
        shown
          ? t("certification.module.videos.title", {
              number: shown.number,
              title: shown.video.title,
            })
          : undefined
      }
    >
      <div className="flex flex-col gap-300">
        <div className="module-player flex items-center justify-center">
          {isStarting && <BaseSpinner color="white" />}

          {(isError || (playbackUrl !== null && failedUrl === playbackUrl)) && (
            <p className="module-player-error body-s-regular" role="alert">
              {t("certification.module.player.error")}
            </p>
          )}

          {playbackUrl && failedUrl !== playbackUrl && (
            <CertificationVideoPlayer>
              <VideoSkin className="full-width full-height">
                <Video
                  ref={videoRef}
                  src={playbackUrl}
                  poster={poster}
                  aria-label={shown?.video.title}
                  playsInline
                  onError={() => setFailedUrl(playbackUrl)}
                />
              </VideoSkin>
            </CertificationVideoPlayer>
          )}
        </div>

        {status}
      </div>
    </BaseModal>
  );
};

export default VideoPlayerModal;
